import React, { createRef } from "react";
import PropTypes from "prop-types";
import { LpgCancelOutline } from "@/icons";

import { ThemeContext, ThemeProvider, Theme } from "../Theme";
import { A11yText } from "../A11yText";
import { Button } from "../Button";
import { Portal } from "../Portal";
import { Icon } from "../Icon";

import styles from "./Modal.scss";

class ModalPortal extends React.Component {
  static propTypes = {
    isOpen: PropTypes.bool,
    isVisible: PropTypes.bool,
    renderClosed: PropTypes.bool,
    closable: PropTypes.bool,
    closeText: PropTypes.string,
    trigger: PropTypes.instanceOf(
      typeof Element === "undefined" ? () => {} : Element
    ),
    beforeOpen: PropTypes.func,
    afterClose: PropTypes.func,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node,
    ]).isRequired,
    portalless: PropTypes.bool,
    containerProps: PropTypes.objectOf(PropTypes.any),
    buttonProps: PropTypes.objectOf(PropTypes.any),
    zIndex: PropTypes.number.isRequired,
  };

  static defaultProps = {
    isOpen: true,
    isVisible: false,
    renderClosed: false,
    closable: true,
    closeText: "Schließen",
    trigger: null,
    beforeOpen: null,
    afterClose: null,
    portalless: false,
    containerProps: null,
    buttonProps: null,
  };

  static displayName = "ModalPortal";

  static contextType = ThemeContext;

  constructor(props) {
    const { isVisible } = props;
    super(props);

    this.state = {
      isVisible,
    };

    this.children = props.children;
    this.trigger =
      props.trigger ||
      (typeof document !== "undefined" && document.activeElement);

    this.wrapperRef = createRef();
    this.modalRef = createRef();
    this.boundClose = this.close.bind(this);
  }

  componentDidMount() {
    const { beforeOpen } = this.props;
    if (beforeOpen) {
      beforeOpen();
    }

    if (this.modalRef.current) {
      this.modalRef.current.focus();
    }
    if (this.wrapperRef.current) {
      this.wrapperRef.current.addEventListener("click", this.checkOverlayClick);
    }
    document.addEventListener("keydown", this.checkDocumentKeyDown);
    document.addEventListener("keydown", this.handleTabKey);

    this.setState({
      isVisible: true,
    });
  }

  componentDidUpdate(prevProps) {
    const { isOpen } = this.props;
    const { isVisible } = this.state;
    if (prevProps.isOpen && !isOpen) {
      this.close();
    }
    if (isOpen && !prevProps.isOpen && !isVisible) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isVisible: true });
    }
  }

  componentWillUnmount() {
    this.trigger.focus();

    if (this.wrapperRef.current) {
      this.wrapperRef.current.removeEventListener(
        "click",
        this.checkOverlayClick
      );
    }
    document.removeEventListener("keydown", this.handleTabKey);
    document.removeEventListener("keydown", this.checkDocumentKeyDown);
  }

  checkOverlayClick = (event) => {
    const { closable } = this.props;
    if (!closable) {
      return;
    }
    if (!this.modalRef.current.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }
  };

  checkDocumentKeyDown = (event) => {
    const { closable } = this.props;
    if (!closable) {
      return;
    }
    if (event.key === "Escape" || event.key === "Esc" || event.keyCode === 27) {
      this.close();
    }
  };

  handleTabKey = (event) => {
    const { isVisible } = this.state;

    if (!isVisible) {
      return true;
    }

    if (this.modalRef.current) {
      const focusableModalElements = this.modalRef.current.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
      );
      const firstElement = focusableModalElements[0];
      const lastElement =
        focusableModalElements[focusableModalElements.length - 1];

      if (
        event.shiftKey &&
        event.code === "Tab" &&
        document.activeElement === firstElement
      ) {
        lastElement.focus();
        return event.preventDefault();
      }

      if (
        !event.shiftKey &&
        event.code === "Tab" &&
        document.activeElement === lastElement
      ) {
        firstElement.focus();
        event.preventDefault();
      }
    }

    return true;
  };

  afterClose = () => {
    this.modalRef.current.removeEventListener("transitionend", this.afterClose);

    const { afterClose } = this.props;
    if (afterClose) {
      afterClose();
    }
  };

  close() {
    this.modalRef.current.addEventListener("transitionend", this.afterClose);

    this.setState({
      isVisible: false,
    });
  }

  /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
  // Disabled because the modal gets focused on mount. Setting focus on a div is only working with a tabIndex
  render() {
    const {
      isVisible: isVisibleProp,
      isOpen,
      renderClosed,
      closable,
      closeText,
      trigger,
      beforeOpen,
      afterClose,
      portalless,
      children,
      containerProps,
      buttonProps,
      zIndex,
      ...others
    } = this.props;
    const { isVisible } = this.state;
    const PortalTag = portalless ? "div" : Portal;
    return (
      <PortalTag>
        <ThemeProvider value={Theme.light}>
          <div
            className={`${styles.modal}${
              isVisible && isOpen ? " is-visible" : ""
            }`}
            ref={this.wrapperRef}
            style={{ zIndex: isVisible && isOpen ? zIndex : -1 }}
            {...containerProps}
          >
            <div
              className={`${styles.cover}${
                isVisible && isOpen ? " is-visible" : ""
              }`}
            />

            <div className={styles.container}>
              <div
                className={`${styles.inner}${
                  isVisible && isOpen ? " is-visible" : ""
                }`}
                role="dialog"
                aria-modal="true"
                ref={this.modalRef}
                tabIndex={0}
                {...others}
              >
                {closable && (
                  <Button
                    variants={[Button.Variant.clean, Button.Variant.reactangle]}
                    customClass={styles.close}
                    type="button"
                    onClick={this.boundClose}
                    {...buttonProps}
                  >
                    <Icon icon={LpgCancelOutline} size={Icon.Size.medium} />
                    <A11yText>{closeText}</A11yText>
                  </Button>
                )}

                <div className={styles.content}>{children}</div>
              </div>
            </div>
          </div>
        </ThemeProvider>
      </PortalTag>
    );
  }
  /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
}

export default ModalPortal;
